import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { intersection } from 'es-toolkit/compat';
import { useCallback, useMemo } from 'react';

import { corePayorPayeeClient } from 'app/api/apiClient/generated/corePayorPayeeClient';
import { settlementAcceptedTermsClient } from 'app/api/apiClient/settlement/settlementAcceptedTermsClient';
import { settlementBillsClient } from 'app/api/apiClient/settlement/settlementBillsClient';
import { settlementCarrierDenyListClient } from 'app/api/apiClient/settlement/settlementCarrierDenyListClient';
import {
  BillsBillCreatePayload,
  BillsBillResponse,
  BillsBillTotal,
  BillsBillTotals,
  BillsBillUpdatePayload,
  BillsPresentResponse,
  BillState,
  BillsUpdateBillByPayeePayload,
} from 'app/api/apiClient/settlement/types';
import { queries } from 'app/constants/queries';
import { UserRoles } from 'app/constants/user';
import { useMutationEnhanced } from 'app/hooks/useMutationEnhanced';
import { useQueryEnhanced } from 'app/hooks/useQueryEnhanced';
import { useAlertsDispatch } from 'app/state/AlertsContext/hooks';
import { useAuthState, useIsPayeeAndOnlyDriver, useIsTriumphInternal } from 'app/state/AuthContext/hooks';
import { usePaperworkDispatch } from 'app/state/PaperworkContext/hooks';
import { handleErrors, handleSuccess } from 'app/utils/lib/context';

import { useCreateCharge } from './charges';
import { useGetChargeType } from './types';
import { useGetUser, useIsPayee } from '../../core/v1/auth/user';
import { useGetPayorNames } from '../../core/v1/payee/payors';

// Local Typings
type BillStateTotals = Record<BillState, number>;

// Local Variables
function setBill(
  queryClient: QueryClient,
  data: BillsBillResponse,
) {
  queryClient.setQueryData<BillsBillResponse>([queries.billsShow, data.bill.id], data);
}

const initialBillStateTotals: BillStateTotals = {
  approved: 0,
  delivered: 0,
  exception: 0,
  ignored: 0,
  incomplete: 0,
  rejected: 0,
  removed: 0,
  sent: 0,
  submitted: 0,
};

function reduceBillStateTotals(totals: BillsBillTotals): BillStateTotals {
  return Object.entries(totals)
    .reduce<BillStateTotals>((acc, [k, v]: [BillState, BillsBillTotal | undefined]) => {
      acc[k] = v ? v.count : 0;

      return acc;
    },
    { ...initialBillStateTotals });
}

export const useIsPermittedPayor = () => {
  const { authRoles } = useAuthState();
  const allowedRoleIds = useMemo(() => [UserRoles.PayorSubmission, UserRoles.UserAdmin],
    []);

  return useMemo(() => intersection(authRoles,
    allowedRoleIds).length > 0,
  [allowedRoleIds, authRoles]);
};

export const useHasBillPermissions = (options?: { allowUnacceptedTerms: boolean }): boolean => {
  const { data: billsPresentData } = useGetBillsPresent();
  const isPermittedPayor = useIsPermittedPayor();

  const isPayee = useIsPayee();
  const userIsTriumph = useIsTriumphInternal();

  const hasAcceptedTermsRequired = !options?.allowUnacceptedTerms;
  const meetsTermsRequirement = !!billsPresentData?.hasAcceptedTerms || !hasAcceptedTermsRequired;

  const isPermittedPayorAndMeetsTerms = isPermittedPayor && meetsTermsRequirement;

  return isPayee || userIsTriumph || isPermittedPayorAndMeetsTerms;
};

export const useGetBillQuery = (id: string | undefined) => {
  const hasPermission = useHasBillPermissions();

  const queryFn = useCallback(() => settlementBillsClient.getBill(id ?? ''),
    [id]);

  return useQueryEnhanced(
    [queries.billsShow, id],
    queryFn,
    {
      enabled: hasPermission && !!id,
    },
  );
};

export const useGetPaperworkType = (id: string | undefined) => {
  const hasPermission = useHasBillPermissions();

  const queryFn = useCallback(() => settlementBillsClient.getBillPaperworkType(id ?? ''),
    [id]);

  const { data } = useQueryEnhanced(
    [queries.billsPaperworkTypeShow, id],
    queryFn,
    {
      enabled: hasPermission && !!id,
    },
  );

  return data;
};

export const useGetValidationRules = (id: string | undefined) => {
  const hasPermission = useHasBillPermissions();

  const queryFn = useCallback(() => settlementBillsClient.getValidationRules(id ?? ''),
    [id]);

  const { data } = useQueryEnhanced(
    [queries.billsValidationRulesShow, id],
    queryFn,
    {
      enabled: hasPermission && !!id,
    },
  );

  return data;
};

export const useGetBill = (id: string | undefined) => {
  const { data: billData } = useGetBillQuery(id);

  return billData?.bill;
};

export const useCanViewPaperworkQuery = () => {
  const user = useGetUser();
  const isPayee = useIsPayee();

  const queryFn = useCallback(() => settlementCarrierDenyListClient.getIsOnDenyList(user?.company?.id ?? ''),
    [user]);

  return useQueryEnhanced(
    [queries.billsCanViewPaperwork],
    queryFn,
    {
      enabled: isPayee,
    },
  );
};

export const useCanViewPaperwork = () => {
  const { data, isFetched } = useCanViewPaperworkQuery();

  return useMemo(() => isFetched ? !data?.isBlacklisted : null,
    [data, isFetched]);
};

export const useGetBillsAll = () => {
  const hasBillPermissions = useHasBillPermissions();

  const isPayee = useIsPayee();

  const queryFn = useCallback(() =>
    settlementBillsClient.getBills({
      excludeTotalAmount: true,
      exportReport: undefined,
      includeTotals: isPayee ? true : false,
      page: undefined,
      pageSize: undefined,
      /* eslint-disable @typescript-eslint/naming-convention */
      'q[created_at_dategteq]': undefined,
      'q[created_at_datelteq]': undefined,
      'q[load_number_or_invoice_number_cont]': undefined,
      'q[payee_id_in][]': undefined,
      'q[payor_id_in][]': undefined,
      'q[state_matches_any][]': undefined,
      /* eslint-enable @typescript-eslint/naming-convention */
      sortAsc: undefined,
      sortField: undefined,
    }),
  [isPayee]);

  return useQueryEnhanced([queries.billsAll],
    queryFn, {
      enabled: hasBillPermissions,
    });
};

export const useGetBillsPresent = () => {
  const isPermittedPayor = useIsPermittedPayor();

  return useQueryEnhanced(
    [queries.billsPresent],
    settlementBillsClient.getBillsPresent,
    {
      enabled: isPermittedPayor,
    },
  );
};

export const useHasAcceptedTerms = () => {
  const { data: billsPresentData } = useGetBillsPresent();

  return billsPresentData?.hasAcceptedTerms;
};

export const useGetBillsPayors = () => {
  const {
    data,
    isInitialLoading: isBillsLoading,
  } = useGetBillsAll();

  const payorIds = useMemo(() => data?.payorIds ?? [],
    [data]);

  const {
    data: payorNameData,
    isInitialLoading: isPayorNamesLoading,
  } = useGetPayorNames(payorIds);

  return {
    data: payorNameData,
    isInitialLoading: isBillsLoading || isPayorNamesLoading,
  };
};

export const useGetBillsPayees = () => {
  const { data: billsData } = useGetBillsAll();
  const hasPermission = useHasBillPermissions();

  const payeeIds = billsData?.payeeIds ?? Array.from(new Set(billsData?.bills.map(bill => bill.payeeId)));

  const queryFn = useCallback(() => corePayorPayeeClient.lookupNames({
    ids: payeeIds ?? [],
  }),
  [payeeIds]);

  return useQueryEnhanced(
    [queries.payorPayeeNames, 'bills', payeeIds],
    queryFn,
    {
      enabled: hasPermission && !!payeeIds?.length,
    },
  );
};

export const useGetBillPayee = (payeeId: string) => {
  const { data, isLoading } = useGetBillsPayees();

  const payeeNameData = useMemo(() => data?.names.find(payee => payee.id === payeeId),
    [data, payeeId]);

  return useMemo(() => ({
    data: payeeNameData,
    isLoading,
  }),
  [isLoading, payeeNameData]);
};

export const useGetBillsStateCounts = () => {
  const {
    data: billsData,
    ...rest
  } = useGetBillsAll();

  const isDriver = useIsPayeeAndOnlyDriver();

  const totals = useMemo(() =>
    billsData?.totals
      ? reduceBillStateTotals(billsData.totals)
      : initialBillStateTotals,
  [billsData]);

  const {
    approved,
    delivered,
    exception,
    ignored,
    incomplete,
    rejected,
    removed,
    sent,
    submitted,
  } = totals;

  const data = useMemo((): Partial<BillStateTotals> => isDriver
    ? {
      incomplete,
      submitted: delivered + submitted + rejected + approved + sent,
    }
    : {
      incomplete: incomplete + sent,
      // the order of these keys determines the order they're displayed
      // eslint-disable-next-line sort-keys
      exception: exception + rejected,
      submitted: delivered + submitted + approved + removed + ignored,
    },
  [approved, delivered, exception, ignored, incomplete, isDriver, rejected, removed, sent, submitted]);

  return useMemo(() => ({
    data,
    ...rest,
  }),
  [data, rest]);
};

export const useUpdateBill = () => {
  const queryClient = useQueryClient();

  const mutationFn = ({
    id,
    payload,
  }: {
    id: string;
    payload: BillsBillUpdatePayload;
  }) => settlementBillsClient.updateBill(id, payload);

  return useMutationEnhanced(mutationFn,
    {
      invalidateTableQueryPrefixes: [queries.billsAll, queries.billsIndex],
      onSuccess: data => {
        setBill(queryClient, data);
      },
    });
};

export const useCreateBill = () => {
  const lineHaulChargeType = useGetChargeType('linehaul');
  const queryClient = useQueryClient();

  const {
    mutateAsync: createCharge,
  } = useCreateCharge();

  const mutationFn = useCallback(async ({
    lineHaulAmount,
    payload,
  }: {
    lineHaulAmount: number | undefined;
    payload: BillsBillCreatePayload;
  }) => {
    const createResponse = await settlementBillsClient.createBill(payload);

    const billId = createResponse.data.bill.id;

    if (lineHaulChargeType && lineHaulAmount) {
      const chargeResponse = await createCharge({
        charge: {
          amount: lineHaulAmount,
          billId,
          chargeTypeId: lineHaulChargeType.id,
        },
      });

      createResponse.data.bill.charges.push(chargeResponse.charge);
    }

    queryClient.setQueryData<BillsBillResponse>([queries.billsShow, billId],
      createResponse.data);

    return createResponse;
  },
  [createCharge, lineHaulChargeType, queryClient]);

  return useMutationEnhanced(mutationFn,
    {
      invalidateTableQueryPrefixes: [queries.billsIndex],
      onSuccess: () => {
        void queryClient.invalidateQueries([queries.billsAll]);
      },
    });
};

export const useDeleteBill = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutationEnhanced(settlementBillsClient.deleteBill,
    {
      invalidateTableQueryPrefixes: [queries.billsAll, queries.billsIndex],
      onSuccess: (_data, billId) => {
        queryClient.removeQueries([queries.billsShow, billId]);
        onSuccess?.();
      },
    });
};

export const useUpdateBillByPayee = () => {
  const paperworkDispatch = usePaperworkDispatch();
  const queryClient = useQueryClient();
  const alertsDispatch = useAlertsDispatch();

  const mutationFn = ({
    id,
    payload,
  }: {
    id: string;
    payload: BillsUpdateBillByPayeePayload;
  }) => settlementBillsClient.updateBillByPayee(id, payload);

  return useMutationEnhanced(mutationFn,
    {
      invalidateTableQueryPrefixes: [queries.billsIndex],
      onError: (error: Record<string, unknown>) => {
        handleErrors({
          dispatch: alertsDispatch,
          errorObject: error,
          expectedKeys: [],
        });
        paperworkDispatch({
          payload: {
            isBillSubmitting: false,
          },
          type: 'setIsBillSubmitting',
        });
      },
      onMutate: () => {
        paperworkDispatch({
          payload: {
            isBillSubmitting: true,
          },
          type: 'setIsBillSubmitting',
        });
      },
      onSuccess: data => {
        paperworkDispatch({
          payload: {
            isBillSubmitting: false,
          },
          type: 'setIsBillSubmitting',
        });
        setBill(queryClient, data);
        void queryClient.invalidateQueries([queries.billsAll]);
      },
    });
};

export const useAcceptTerms = () => {
  const queryClient = useQueryClient();
  const alertsDispatch = useAlertsDispatch();

  return useMutationEnhanced(settlementAcceptedTermsClient.acceptTerms,
    {
      onSuccess: () => {
        const billsPresentData = queryClient.getQueryData<BillsPresentResponse>([queries.billsPresent]);

        if (billsPresentData) {
          queryClient.setQueryData<BillsPresentResponse>([queries.billsPresent],
            {
              ...billsPresentData,
              hasAcceptedTerms: true,
            });
        }

        handleSuccess({
          dispatch: alertsDispatch,
          successMessage: 'Paperwork Accepted!',
        });
      },
    });
};

export const useResubmitBills = () => {
  return useMutationEnhanced(settlementBillsClient.resubmitBills);
};
